import React, {useState} from 'react';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import styles from './ClientBillingGraph.module.css';
import ClientBillingTable from "./ClientBillingTable";
import {PaymentTableResponseData} from "../../../types/client";
import {useAlert} from "../../common/error/AlertContext";
import {getReceiptAPI} from "../../../services/client";

interface GetAdminGraphResponseData {
    month: number;
    amount: number;
}

interface ClientBillingGraphProps {
    graph?: GetAdminGraphResponseData[];
    initialYear: number;
    onYearChange?: (year: number) => void;
}

const ClientBillingGraph = ({
                                graph = [],
                                initialYear,
                                onYearChange
                            }: ClientBillingGraphProps) => {
    const [view, setView] = useState<string>('chart');
    const [year, setYear] = useState(initialYear);
    const [selectedTableData, setSelectedTableData] = useState<PaymentTableResponseData[]>([]);
    const {showAlert} = useAlert();

    const years = Array.from(
        {length: new Date().getFullYear() - initialYear + 1},
        (_, i) => new Date().getFullYear() - i
    );

    const formatYAxis = (value: number) => {
        return `₩${value.toFixed(0)}`;
    };

    const formatTooltip = (value: number) => {
        return [`₩${value.toLocaleString()}`, '결제액'];
    };

    const handleYearChange = (newYear: number) => {
        setYear(newYear);
        onYearChange?.(newYear);
    };

    const handleCashReceiptClick = async () => {
        try {
            const response = getReceiptAPI(selectedTableData.map((data) => data.paymentId))
            // showAlert('개발 중인 기능입니다.', "info");
        } catch (error) {
            console.error(error);
        }
    };

    const getTitle = () => {
        return view === 'chart' ? '월별 결제액' : '결제 내역';
    };

    const renderContent = () => {
        if (view === 'table') {
            return <ClientBillingTable
                onSelectedDataChange={setSelectedTableData}/>
        }

        if (!graph || graph.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <p className={styles.emptyText}>결제 내역이 없습니다.</p>
                </div>
            );
        }

        return (
            <ResponsiveContainer width="100%" height={465}>
                <BarChart data={graph} margin={{top: 20, right: 30, left: 20, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                    <XAxis
                        dataKey="month"
                        tickFormatter={(month) => `${month}월`}
                        axisLine={false}
                        className={styles.chartXaxis}
                    />
                    <YAxis
                        tickFormatter={formatYAxis}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip
                        formatter={formatTooltip}
                        cursor={{fill: 'rgba(0, 0, 0, 0.1)'}}
                    />
                    <Bar
                        dataKey="amount"
                        fill="#1E40AF"
                        radius={[4, 4, 0, 0]}
                        barSize={30}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>{getTitle()}</h2>
                <div className={styles.controls}>
                    {view === 'table' ? (
                        <button
                            className={styles.yearButton}
                            onClick={handleCashReceiptClick}
                        >
                            현금영수증 발행
                        </button>
                    ) : (
                        <select
                            value={year}
                            onChange={(e) => handleYearChange(Number(e.target.value))}
                            className={styles.select}
                        >
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}년
                                </option>
                            ))}
                        </select>
                    )}
                    <select
                        value={view}
                        onChange={(e) => setView(e.target.value as 'chart')}
                        className={styles.select}
                    >
                        <option value="chart">Chart</option>
                        <option value="table">Table</option>
                    </select>
                </div>
            </div>
            <div className={styles.graphContainer}>
                {renderContent()}
            </div>
        </div>
    );
};

export default ClientBillingGraph;